import React, { useState, useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom'; // Added useLocation
import { ArrowLeft, Calendar, User, Folder, Tag } from 'lucide-react';
import axios from 'axios';
import SEO from '../components/SEO';
import CommentSection from '../components/CommentSection';

const SingleBlogPage = () => {
  const { slug } = useParams();
  const location = useLocation(); // Used to read the URL for the success message
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState([]); 

  // 1. Fetch the Blog Post
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BASE_URL}/backend/api/posts/${slug}`)
      .then(response => {
        setPost(response.data);
        setLoading(false);
      })
      .catch(error => {
        console.error("Error fetching post:", error);
        setLoading(false);
      });
  }, [slug]);

  // 2. Fetch the Verified Comments once the post loads
  useEffect(() => {
    if (post?.id) {
      axios.get(`${process.env.REACT_APP_BASE_URL}/backend/api/posts/${post.id}/comments`, {
          headers: { 'Accept': 'application/json' }
      })
      .then(res => setComments(res.data))
      .catch(err => console.error("Error fetching comments:", err));
    }
  }, [post?.id]);

  if (loading) {
    return (
      <div className="min-h-screen pt-32 flex justify-center bg-brand-light">
        <div className="w-12 h-12 border-4 border-brand-cyan border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen pt-32 text-center bg-brand-light">
        <h2 className="text-2xl font-bold text-gray-700">Post not found</h2>
        <Link to="/blog" className="text-brand-cyan mt-4 inline-block hover:underline">Return to Blog</Link>
      </div>
    );
  }

  const tagsArray = post.tags ? post.tags.split(',').map(tag => tag.trim()) : [];
  
  // Check if the URL has ?verified=true
  const isJustVerified = new URLSearchParams(location.search).get('verified') === 'true';

  return (
    <>
      <SEO
        title={post.title}
        description={post.meta_description}
      />

      <div className="pt-32 pb-24 min-h-screen bg-brand-light">
        <div className="max-w-4xl mx-auto px-6">

          <Link to="/blog" className="inline-flex items-center gap-2 text-gray-500 hover:text-brand-cyan transition-colors mb-8 font-bold text-sm uppercase tracking-wider">
            <ArrowLeft size={16} /> Back to Insights
          </Link>

          {/* Verification Success Banner */}
          {isJustVerified && (
            <div className="mb-8 p-4 bg-green-100 border border-green-400 text-green-700 font-bold rounded-xl text-center shadow-sm">
              Success! Your comment has been verified and is now live.
            </div>
          )}

          <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100">

            <div className="h-[400px] w-full relative bg-gray-100">
              <img
                src={`${process.env.REACT_APP_BASE_URL}/backend/api/image/${post.image_path}`}
                alt={post.title}
                className="w-full h-full object-cover"
              />
              {post.category && (
                <div className="absolute top-6 left-6 bg-brand-cyan text-brand-dark font-black text-xs uppercase tracking-widest py-2 px-4 rounded-full shadow-lg flex items-center gap-2">
                  <Folder size={14} /> {post.category}
                </div>
              )}
            </div>

            <div className="p-8 md:p-16">

              <div className="flex flex-wrap items-center gap-6 text-sm text-gray-400 font-bold mb-6 uppercase tracking-wider">
                <div className="flex items-center gap-2">
                  <Calendar size={16} className="text-brand-cyan" />
                  {new Date(post.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </div>
                {post.author_name && (
                  <div className="flex items-center gap-2">
                    <User size={16} className="text-brand-cyan" />
                    By {post.author_name}
                  </div>
                )}
              </div>

              <h1 className="text-3xl md:text-5xl font-heading font-black text-brand-dark mb-10 leading-tight">
                {post.title}
              </h1>

              <div
                className="prose prose-lg max-w-none prose-a:text-brand-cyan hover:prose-a:text-brand-dark"
                dangerouslySetInnerHTML={{ __html: post.content }}
              />

              {tagsArray.length > 0 && (
                <div className="pt-8 border-t border-gray-100">
                  <div className="flex items-center gap-3 flex-wrap">
                    <Tag size={18} className="text-gray-400" />
                    {tagsArray.map((tag, index) => (
                      <span
                        key={index}
                        className="bg-gray-50 border border-gray-200 text-gray-600 text-sm font-semibold py-1 px-4 rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Display Verified Comments */}
              <div className="mt-16 mb-12">
                <h3 className="text-2xl font-black text-brand-dark mb-6 border-b border-gray-100 pb-4">
                  Comments ({comments.length})
                </h3>

                {comments.length === 0 ? (
                  <p className="text-gray-500 italic">No comments yet. Be the first to share your thoughts!</p>
                ) : (
                  <div className="space-y-6">
                    {comments.map((comment) => (
                      <div key={comment.id} className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-10 h-10 bg-brand-dark text-brand-cyan font-bold rounded-full flex items-center justify-center">
                            {comment.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <h4 className="font-bold text-brand-dark">{comment.name}</h4>
                            <span className="text-xs text-gray-400">
                              {new Date(comment.created_at).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        <p className="text-gray-600 leading-relaxed">{comment.content}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <CommentSection postId={post.id} />
            </div>
          </div>

        </div>
      </div>
    </>
  );
};

export default SingleBlogPage;